import React, { lazy, Suspense } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import theme from './styles/theme';
import { AuthProvider } from './contexts/AuthContext';
import Layout from './components/layout/Layout';
import PrivateRoute from './components/auth/PrivateRoute';
import AdminRoute from './components/auth/AdminRoute';
import Loading from './components/common/Loading';
import { GoogleAnalytics } from './components/GoogleAnalytics';
import ClarityAnalytics from './components/analytics/ClarityAnalytics';

// Lazy loaded components
const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Tracking = lazy(() => import('./pages/Tracking'));
const Blog = lazy(() => import('./pages/Blog'));
const Contact = lazy(() => import('./pages/Contact'));
const Quote = lazy(() => import('./pages/Quote'));
const QuoteSuccess = lazy(() => import('./pages/QuoteSuccess'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const Profile = lazy(() => import('./pages/Profile'));
const FreightClassCalculatorPage = lazy(() => import('./pages/FreightClassCalculatorPage'));
const Tools = lazy(() => import('./pages/Tools'));
const GlossaryPage = lazy(() => import('./pages/GlossaryPage'));
const FAQ = lazy(() => import('./pages/FAQ'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const Sitemap = lazy(() => import('./pages/Sitemap'));
const TrackShipment = lazy(() => import('./pages/TrackShipment'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const VerifyEmailPending = lazy(() => import('./pages/VerifyEmailPending'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));

function App() {
  return (
    <ChakraProvider theme={theme}>
      <HelmetProvider>
        <Router>
          {/* Add Clarity Analytics */}
          <ClarityAnalytics projectId={import.meta.env.VITE_CLARITY_PROJECT_ID} />
          <AuthProvider>
            <Routes>
              <Route path="/" element={<Layout><Outlet /></Layout>}>
                <Route index element={
                  <Suspense fallback={<Loading />}>
                    <Home />
                  </Suspense>
                } />
                <Route path="login" element={
                  <Suspense fallback={<Loading />}>
                    <Login />
                  </Suspense>
                } />
                <Route path="register" element={
                  <Suspense fallback={<Loading />}>
                    <Register />
                  </Suspense>
                } />
                <Route path="tracking" element={
                  <Suspense fallback={<Loading />}>
                    <Tracking />
                  </Suspense>
                } />
                <Route path="blog" element={
                  <Suspense fallback={<Loading />}>
                    <Blog />
                  </Suspense>
                } />
                <Route path="contact" element={
                  <Suspense fallback={<Loading />}>
                    <Contact />
                  </Suspense>
                } />
                <Route path="quote" element={
                  <Suspense fallback={<Loading />}>
                    <Quote />
                  </Suspense>
                } />
                <Route path="quote/:quoteNumber" element={
                  <Suspense fallback={<Loading />}>
                    <QuoteSuccess />
                  </Suspense>
                } />

                {/* Email Verification Routes */}
                <Route path="verify-email/pending" element={
                  <Suspense fallback={<Loading />}>
                    <VerifyEmailPending />
                  </Suspense>
                } />
                <Route path="verify-email/:token" element={
                  <Suspense fallback={<Loading />}>
                    <VerifyEmail />
                  </Suspense>
                } />

                {/* Password Reset Routes */}
                <Route path="forgot-password" element={
                  <Suspense fallback={<Loading />}>
                    <ForgotPassword />
                  </Suspense>
                } />
                <Route path="reset-password/:token" element={
                  <Suspense fallback={<Loading />}>
                    <ResetPassword />
                  </Suspense>
                } />

                {/* Tools Routes */}
                <Route path="tools" element={
                  <Suspense fallback={<Loading />}>
                    <Tools />
                  </Suspense>
                } />
                <Route path="tools/freight-class-calculator" element={
                  <Suspense fallback={<Loading />}>
                    <FreightClassCalculatorPage />
                  </Suspense>
                } />
                <Route path="tools/glossary" element={
                  <Suspense fallback={<Loading />}>
                    <GlossaryPage />
                  </Suspense>
                } />
                <Route path="faq" element={
                  <Suspense fallback={<Loading />}>
                    <FAQ />
                  </Suspense>
                } />
                <Route path="privacy-policy" element={
                  <Suspense fallback={<Loading />}>
                    <PrivacyPolicy />
                  </Suspense>
                } />
                <Route path="terms-of-service" element={
                  <Suspense fallback={<Loading />}>
                    <TermsOfService />
                  </Suspense>
                } />
                <Route path="cookie-policy" element={
                  <Suspense fallback={<Loading />}>
                    <CookiePolicy />
                  </Suspense>
                } />
                <Route path="sitemap" element={
                  <Suspense fallback={<Loading />}>
                    <Sitemap />
                  </Suspense>
                } />

                {/* Protected Routes */}
                <Route path="dashboard" element={
                  <Suspense fallback={<Loading />}>
                    <PrivateRoute>
                      <Dashboard />
                    </PrivateRoute>
                  </Suspense>
                } />
                <Route path="admin/dashboard" element={
                  <Suspense fallback={<Loading />}>
                    <AdminRoute>
                      <AdminDashboard />
                    </AdminRoute>
                  </Suspense>
                } />
                <Route path="profile" element={
                  <Suspense fallback={<Loading />}>
                    <PrivateRoute>
                      <Profile />
                    </PrivateRoute>
                  </Suspense>
                } />
                <Route path="track" element={
                  <Suspense fallback={<Loading />}>
                    <TrackShipment />
                  </Suspense>
                } />
              </Route>
            </Routes>
          </AuthProvider>
        </Router>
      </HelmetProvider>
    </ChakraProvider>
  );
}

export default App;
